import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import "./FileManager.css";
import DeleteModal from "./DeleteModal.tsx";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../Redux/Store/index.ts";
import { allPut, downloadFiles, moveFiles } from "../Redux/Actions.tsx";
import { useAlert } from "./Alert.tsx";
import { Contratti } from "../lib/interface.ts";
import { urlcontratti } from "../lib/api.ts";
import { ProgressSpinner } from "primereact/progressspinner";

interface FileData {
  fileKey: string;
  url?: string;
  isFolder: boolean;
  id?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  principalFileName?: string;
}

interface FileManagerProps {
  folderKey: string; // p.es. "contrattoabc"
  folderType: string; // p.es. "contratti"
  fileList: FileData[];
  previewFile: File | null;
  setPreviewFile: (file: File | null) => void;
  customFolder: string | null;
  setCustomFolder: (folder: string | null) => void;
  onConfirmUpload: (filePrincipale: boolean) => void;
  onClose: () => void;
  onDeleteSuccess: () => void;
  principalFileName?: string;
  elementContratto?: Contratti;
  fetchFiles?: () => void;
}

const FileManager: React.FC<FileManagerProps> = ({
  folderKey,
  folderType,
  fileList,
  previewFile,
  setPreviewFile,
  customFolder,
  setCustomFolder,
  onConfirmUpload,
  onClose,
  onDeleteSuccess,
  principalFileName,
  elementContratto,
  fetchFiles,
}) => {
  const [newFolderInputVisible, setNewFolderInputVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState<string | null>(null);
  const [fileKeyToDelete, setFileKeyToDelete] = useState<string | null>(null);

  const [newFolderName, setNewFolderName] = useState("");
  const [localFileList, setLocalFileList] = useState<FileData[]>(fileList);
  const [isFilePrincipaleModalOpen, setIsFilePrincipaleModalOpen] =
    useState(false);

  const utentiRedux = useSelector(
    (state: RootState) => state.utenti.utenti || []
  );
  const dispatch: AppDispatch = useDispatch();
  const showAlert = useAlert();
  const { token } = useSelector((state: RootState) => state.login);

  const [draggingFileId, setDraggingFileId] = useState<string | null>(null);
  const [draggingFileKey, setDraggingFileKey] = useState<string | null>(null);
  const [targetFolder, setTargetFolder] = useState<string | null>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState("");
  const newFileName = previewFile ? previewFile.name : "";

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const [isChangePrincipalModalOpen, setIsChangePrincipalModalOpen] =
    useState(false);
  const [selectedFileForStar, setSelectedFileForStar] =
    useState<FileData | null>(null);

  const handleStarClick = (file: FileData) => {
    // Se è già il file principale, non facciamo nulla (o potresti gestire "un-set" del principale).
    if (file.id === elementContratto?.documentoPrincipale) {
      return;
    }
    setSelectedFileForStar(file);
    setIsChangePrincipalModalOpen(true);
  };

  const confirmChangePrincipalFile = async () => {
    if (!selectedFileForStar?.id || !elementContratto) {
      setIsChangePrincipalModalOpen(false);
      return;
    }
    // Aggiorna il contratto
    const aggiornato = {
      ...elementContratto,
      documentoPrincipale: selectedFileForStar.id,
      // Attenzione: Se la tua API si aspetta "documentoPrincipale" = file.id
      // e non "fileKey", allora userai "id".
      // Se invece si aspetta "fileKey", usa:
      // documentoPrincipale: selectedFileForStar.fileKey,
    };

    try {
      await dispatch(
        allPut(
          `${urlcontratti}/${elementContratto.id}`,
          token,
          aggiornato,
          showAlert
        )
      );
      // Ricarica la lista file e chiudi il modal
      fetchFiles && fetchFiles();
      showAlert &&
        showAlert(
          "success",
          "Successo",
          "File principale aggiornato con successo!",
          "bottom-end",
          2500
        );
    } catch (error) {
      console.error("Errore nel cambiare il file principale:", error);
    }
    setIsChangePrincipalModalOpen(false);
    setSelectedFileForStar(null);
    window.location.reload();
  };

  /** Torna alla cartella superiore. */
  const goBackOneLevel = () => {
    if (!customFolder) return;
    const parts = customFolder.split("/");
    parts.pop();
    setCustomFolder(parts.length > 0 ? parts.join("/") : null);
  };

  /** Cartella "padre" (un livello sopra). */
  const parentFolder = customFolder
    ? (() => {
        const parts = customFolder.split("/");
        parts.pop();
        return parts.length > 0 ? parts.join("/") : "";
      })()
    : null;

  /** Ricarica localFileList ogni volta che fileList o customFolder cambiano. */
  useEffect(() => {
    setLocalFileList(fileList);
  }, [fileList, customFolder]);

  const handleCreateFolder = () => {
    setNewFolderInputVisible(true);
  };

  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(e.target.value);
  };

  return (
    <>
      <Modal show onHide={onClose} size="xl" centered>
        {isChangePrincipalModalOpen && (
          <div className="modal-backdrop-custom"></div>
        )}
        <Modal.Header className="file-header d-flex justify-content-between align-items-center">
          <h5>
            File nella cartella: {folderKey}
            {customFolder && ` / ${customFolder}`}
          </h5>
          <div className="d-flex align-items-center">
            {customFolder && (
              <Button
                variant="secondary"
                className="button btn-sm me-2"
                onClick={goBackOneLevel}
              >
                <i
                  className="pi pi-chevron-left fs-24"
                  style={{ cursor: "pointer" }}
                ></i>
              </Button>
            )}
            <Button
              variant="primary"
              className="button btn-sm me-2"
              onClick={handleCreateFolder}
            >
              + Nuova cartella
            </Button>

            {/* Nuovo pulsante "Aggiungi file" */}
            <Button
              variant="primary"
              className="button btn-sm me-2"
              onClick={() => fileInputRef.current?.click()}
            >
              + Aggiungi file
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setPreviewFile(file);
                }
              }}
            />

            <button
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              style={{ fontSize: "1.2rem", color: "black" }}
            ></button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {(isMoveModalOpen ||
            isDeleteModalOpen ||
            isFilePrincipaleModalOpen) && (
            <div className="modal-backdrop-custom"></div>
          )}
          <div className="file-manager">
            {customFolder && parentFolder !== null && (
              <Row
                className="file-item align-items-center py-2"
                style={{
                  backgroundColor: "#f7f7f7",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  marginBottom: "10px",
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggingFileId && parentFolder !== null) {
                    // Sposta verso la cartella superiore
                    setTargetFolder(parentFolder);
                    setIsMoveModalOpen(true);
                  }
                }}
              >
                {localFileList.length > 0 && (
                  <Col xs={12} className="text-center">
                    <b>Rilascia qui per spostare nella cartella superiore</b>
                  </Col>
                )}
              </Row>
            )}

            {localFileList.length > 0 ? (
              localFileList.map((file, index) => {
                const fileOwner = utentiRedux.find(
                  (u) => u.id === file.uploadedBy
                );
                return (
                  <Row
                    key={index}
                    className="file-item align-items-center py-2"
                    draggable={!file.isFolder}
                    style={{ cursor: !file.isFolder ? "grab" : "default" }}
                    onDragStart={(e) => {
                      if (!file.isFolder && file.id) {
                        setDraggingFileId(file.id);
                        setDraggingFileKey(file.fileKey);
                        e.currentTarget.style.cursor = "grabbing";
                      }
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.cursor = !file.isFolder
                        ? "grab"
                        : "default";
                    }}
                    onDragOver={(e) => {
                      if (file.isFolder) e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggingFileId && file.isFolder) {
                        // Spostiamo dentro questa cartella
                        setTargetFolder(file.fileKey);
                        setIsMoveModalOpen(true);
                      }
                    }}
                    onDoubleClick={() => {
                      if (!file.isFolder && file.url) {
                        window.open(file.url, "_blank");
                      }
                    }}
                  >
                    <Col xs={2} className="text-center bor-rig">
                      <img
                        src={
                          file.isFolder
                            ? "../../assets/IconFolder.svg"
                            : "../../assets/IconFile.svg"
                        }
                        alt={file.isFolder ? "Folder Icon" : "File Icon"}
                        className="file-icon"
                        style={{
                          cursor: file.isFolder ? "pointer" : "grab",
                        }}
                        onClick={() => {
                          if (file.isFolder) {
                            // Se la cartella è "livelloprimary", e customFolder= "livelloprimary",
                            // allora "file.fileKey" potrebbe essere "livellosecondary" => entriamo in "livelloprimary/livellosecondary"
                            if (!customFolder) {
                              setCustomFolder(file.fileKey);
                            } else {
                              // Evita di duplicare se "file.fileKey" è già "livelloprimary/something"
                              // Se fileKey NON ha slash => p.es. "livellosecondary", allora append
                              // Se invece fileKey fosse "livelloprimary/livellosecondary", evitiamo duplicazione
                              if (file.fileKey.includes("/")) {
                                // se file.fileKey è già un path relativo, ci entriamo direttamente
                                setCustomFolder(file.fileKey);
                              } else {
                                setCustomFolder(
                                  `${customFolder}/${file.fileKey}`
                                );
                              }
                            }
                          }
                        }}
                      />
                    </Col>

                    <Col xs={3} className="text-truncate bor-rig">
                      <span
                        style={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {file.fileKey.split("/").pop()}
                      </span>
                    </Col>

                    <Col xs={3} className="text-center bor-rig">
                      {file.isFolder ? (
                        <b></b>
                      ) : (
                        <b>
                          {fileOwner
                            ? `${fileOwner.nome} ${fileOwner.cognome}`
                            : ""}
                        </b>
                      )}
                    </Col>

                    <Col xs={2} className="text-center bor-rig">
                      {file.isFolder ? (
                        <b></b>
                      ) : file.uploadedAt ? (
                        <b>
                          {new Date(file.uploadedAt).toLocaleDateString(
                            "it-IT",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </b>
                      ) : (
                        <b>Data non disponibile</b>
                      )}
                    </Col>

                    <Col xs={2} className="text-center bor-rig">
                      {folderType === "contratti" &&
                        !file.isFolder &&
                        file.id && (
                          <i
                            className={
                              elementContratto?.documentoPrincipale === file.id
                                ? "pi pi-star-fill fs-20 me-3"
                                : "pi pi-star fs-20 me-3"
                            }
                            style={{
                              color:
                                elementContratto?.documentoPrincipale ===
                                file.id
                                  ? "#ef5913"
                                  : "#a6a9b3",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              if (
                                elementContratto?.documentoPrincipale !==
                                file.id
                              ) {
                                handleStarClick(file);
                              }
                            }}
                          />
                        )}

                      {!file.isFolder && file.id && (
                        <>
                          <i
                            className="pi pi-trash me-3 fs-24"
                            style={{ color: "#a6a9b3", cursor: "pointer" }}
                            onClick={() => {
                              setFileIdToDelete(file.id!);
                              setFileKeyToDelete(file.fileKey);
                              setIsDeleteModalOpen(true);
                            }}
                          ></i>
                          <i
                            className="pi pi-download me-3 fs-24"
                            onClick={async () => {
                              if (file.id) {
                                setIsLoadingDownload(true);
                                const result = await dispatch(
                                  downloadFiles(
                                    folderType,
                                    folderKey,
                                    file.id,
                                    file.fileKey,
                                    token,
                                    showAlert
                                  ) as any
                                );
                                setIsLoadingDownload(false);
                                if (result !== true) {
                                  showAlert &&
                                    showAlert(
                                      "danger",
                                      "Errore",
                                      "Impossibile scaricare il file.",
                                      "bottom-end",
                                      3000
                                    );
                                }
                              }
                            }}
                            style={{ color: "#a6a9b3", cursor: "pointer" }}
                          ></i>
                          <img
                            src="../../assets/IconSend.svg"
                            alt="Condividi"
                            style={{
                              fontSize: "24px",
                              cursor: "pointer",
                              color: "#a6a9b3",
                            }}
                            onClick={() => {
                              if (!file.isFolder && file.url) {
                                // Apri il client di posta con subject e body personalizzati
                                const subject = encodeURIComponent(
                                  `Condivisione del file: ${file.fileKey
                                    .split("/")
                                    .pop()}`
                                );
                                const body = encodeURIComponent(
                                  `Ciao,\n\necco il link per accedere al file:\n${file.url}\n\nSaluti!`
                                );
                                window.location.href = `mailto:?subject=${subject}&body=${body}`;
                              }
                            }}
                          />
                        </>
                      )}
                    </Col>
                  </Row>
                );
              })
            ) : (
              <p>Nessun file trovato nella cartella.</p>
            )}

            {newFolderInputVisible && (
              <Row
                className="file-item align-items-center py-2"
                style={{
                  backgroundColor: "rgba(240, 240, 240, 1)",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  marginTop: "10px",
                }}
              >
                <Col xs={2} className="text-center">
                  <img
                    src="../../assets/IconFolder.svg"
                    alt="Folder Icon"
                    className="file-icon"
                  />
                </Col>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    placeholder="Inserisci nome della cartella"
                    value={newFolderName}
                    onChange={handleFolderNameChange}
                    autoFocus
                    style={{ width: "90%" }}
                  />
                </Col>
                <Col xs={2} className="text-end">
                  <Button
                    className="button"
                    size="sm"
                    onClick={() => {
                      const trimmed = newFolderName.trim();
                      if (!trimmed) {
                        setNewFolderInputVisible(false);
                        setNewFolderName("");
                        return;
                      }
                      // Costruisci il fileKey per la cartella
                      // Se customFolder= "livelloprimary", la nuova cartella = "livelloprimary/nuova"
                      const newFolderFileKey = customFolder
                        ? `${customFolder}/${trimmed}`
                        : trimmed;

                      setLocalFileList((prev) => [
                        ...prev,
                        {
                          fileKey: newFolderFileKey,
                          isFolder: true,
                        } as FileData,
                      ]);

                      setNewFolderInputVisible(false);
                      setNewFolderName("");
                    }}
                  >
                    Crea cartella
                  </Button>
                </Col>
              </Row>
            )}

            {previewFile && (
              <Row
                className="file-item align-items-center py-2"
                style={{
                  backgroundColor: "rgba(239,89,19, 0.2)",
                  borderRadius: "5px",
                  marginBottom: "10px",
                }}
              >
                <Col xs={2} className="text-center bor-rig">
                  <img
                    src="../../assets/IconFile.svg"
                    alt="File Icon"
                    className="file-icon"
                  />
                </Col>
                <Col xs={4} className="text-truncate bor-rig">
                  <span
                    style={{
                      display: "inline-block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {previewFile.name}
                  </span>
                </Col>
                <Col xs={4} className="text-end"></Col>
                <Col xs={2} className="text-end d-flex justify-content-center">
                  <Button
                    className="button"
                    size="lg"
                    onClick={() => {
                      if (folderType === "contratti") {
                        setIsFilePrincipaleModalOpen(true);
                      } else {
                        onConfirmUpload(true);
                      }
                    }}
                  >
                    Salva
                  </Button>
                </Col>
              </Row>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {isLoadingDownload && (
        <div className="uploading-overlay">
          <ProgressSpinner />
          <p>Caricamento in corso...</p>
        </div>
      )}

      {isFilePrincipaleModalOpen && (
        <Modal show onHide={() => setIsFilePrincipaleModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>File Principale</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Vuoi che questo sia il file principale di <b>{folderKey}</b>
              {principalFileName && principalFileName !== "" && (
                <>
                  , sostituendo <b>{principalFileName}</b>
                </>
              )}
              ?
            </p>
            {/* {principalFileName && principalFileName !== "" && (
              <>
                <p>
                  Per confermare, inserisci il nome del nuovo file principale:{" "}
                  <b>({newFileName})</b>
                </p>
                <Form.Control
                  type="text"
                  placeholder={`Inserisci "${newFileName}"`}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </>
            )} */}
          </Modal.Body>
          <Modal.Footer
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Button
              className="button"
              onClick={() => {
                setIsFilePrincipaleModalOpen(false);
                onConfirmUpload(false);
              }}
            >
              Non renderlo il file principale
            </Button>
            <Button
              className="button"
              disabled={false}
              onClick={() => {
                setIsFilePrincipaleModalOpen(false);
                onConfirmUpload(true);
              }}
            >
              Rendilo il file principale
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {isDeleteModalOpen && fileIdToDelete && fileKeyToDelete && (
        <DeleteModal
          type="file"
          idObj={fileIdToDelete || ""}
          matchIdentifier={fileKeyToDelete || ""}
          show={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setFileIdToDelete(null);
            setFileKeyToDelete(null);
          }}
          onDeleteSuccess={() => {
            if (fileIdToDelete === elementContratto?.documentoPrincipale) {
              const updatedContratto = {
                ...elementContratto,
                documentoPrincipale: "",
              };
              dispatch(
                allPut(
                  `${urlcontratti}/${elementContratto.id}`,
                  token,
                  updatedContratto
                )
              ).catch(() => {
                // ...
              });
            }
            if (fetchFiles) {
              fetchFiles();
            }
          }}
        />
      )}

      {/* Modale di conferma per cambiare file principale */}
      <Modal
        show={isChangePrincipalModalOpen}
        onHide={() => setIsChangePrincipalModalOpen(false)}
        centered
        backdropClassName="my-custom-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Conferma file principale</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Sei sicuro di voler impostare questo file come principale?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="button" onClick={confirmChangePrincipalFile}>
            Sì, imposta come principale
          </Button>
          <Button
            variant="secondary"
            onClick={() => setIsChangePrincipalModalOpen(false)}
          >
            Annulla
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={isMoveModalOpen}
        onHide={() => setIsMoveModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Conferma spostamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Vuoi spostare il file/cartella "{draggingFileKey}" nella cartella "
            {targetFolder}"?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsMoveModalOpen(false);
              setDraggingFileId(null);
              setDraggingFileKey(null);
              setTargetFolder(null);
            }}
          >
            Annulla
          </Button>
          <Button
            className="button"
            onClick={() => {
              if (draggingFileId && targetFolder !== null) {
                // Costruiamo il path finale
                // Base: folderType/folderKey => "contratti/contrattoabc"
                const basePath = `${folderType}/${folderKey}`;
                // Se ho customFolder => es. "livelloprimary"
                const currentFolderPath = customFolder
                  ? `${basePath}/${customFolder}`
                  : basePath;

                let finalPath = currentFolderPath;

                // Se targetFolder === parentFolder => salgo di un livello
                if (targetFolder === parentFolder) {
                  finalPath = parentFolder
                    ? `${basePath}/${parentFolder}`
                    : basePath;
                }
                // Se sto spostando dentro una cartella "targetFolder"
                else if (targetFolder !== parentFolder && targetFolder !== "") {
                  // Se targetFolder è già un path, es. "livelloprimary/livellosecondary"
                  if (targetFolder.includes("/")) {
                    finalPath = `${basePath}/${targetFolder}`;
                  } else {
                    // Altrimenti lo appendo
                    finalPath += `/${targetFolder}`;
                  }
                }

                dispatch(moveFiles(token, draggingFileId, finalPath, showAlert))
                  .then((result) => {
                    if (result) {
                      onDeleteSuccess();
                    }
                  })
                  .finally(() => {
                    setDraggingFileId(null);
                    setDraggingFileKey(null);
                    setTargetFolder(null);
                    setIsMoveModalOpen(false);
                  });
              }
            }}
          >
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FileManager;
